import type { AxiosInstance } from 'axios';

/**
 * Migration controller wrapper.
 *
 * Maps the §10 and §12 backend prompts in `BACKEND_AGENT_PROMPTS.md`
 * onto typed client calls so the signup wizard, the grant-admin page,
 * and the auto-tests don't all hand-roll their own URL strings.
 *
 *   §10  POST /api/admin/migration/run
 *   §11  POST /api/admin/migration/seed-menus
 *   §12  POST /api/admin/migration/grant-admin-everything
 *
 * Every endpoint returns a small envelope that the panel renders into
 * progress / KPI cards. Errors are surfaced verbatim — the controller
 * is required to include the failing SQL message + connection-string
 * fingerprint (without password).
 */

/* -------------------------------------------------------------------- */
/*  §10 — Run migrations                                                */
/* -------------------------------------------------------------------- */

export interface MigrationRunInput {
  /** Optional — backend mints a fresh GUID when omitted. */
  tenantId?: string;
  dataSource: string;
  initialCatalog: string;
  userId?: string;
  password?: string;
  integratedSecurity?: boolean;
  minPoolSize?: number;
  maxPoolSize?: number;
  connectTimeout?: number;
  trustServerCertificate?: boolean;
  encrypt?: boolean;
  applicationName?: string;
}

export interface MigrationRunResult {
  tenantId: string;
  applied: number;
  durationMs: number;
  /** Optional — present when the run short-circuited because schema was already current. */
  shortCircuited?: boolean;
}

/* -------------------------------------------------------------------- */
/*  §11 — Seed menus / permissions / roles                              */
/* -------------------------------------------------------------------- */

export interface SeedMenusInput {
  tenantId: string;
  /** Re-seed even when rows already exist. Default false (idempotent merge). */
  force?: boolean;
}

export interface SeedMenusResult {
  tenantId: string;
  menus: { inserted: number; updated: number; skipped: number };
  permissions: { inserted: number; updated: number; skipped: number };
  roles: { inserted: number; updated: number; skipped: number };
  rolePermissions: { inserted: number; updated: number; skipped: number };
  durationMs: number;
}

/* -------------------------------------------------------------------- */
/*  §12 — Grant the bootstrap admin everything                          */
/* -------------------------------------------------------------------- */

export interface GrantAdminInclude {
  roles?: boolean;
  permissions?: boolean;
  menus?: boolean;
  branches?: boolean;
  warehouses?: boolean;
}

export interface GrantAdminInput {
  tenantId?: string;
  /** Either userId or username must be set. */
  userId?: string;
  username?: string;
  include?: GrantAdminInclude;
}

export interface GrantAdminStage {
  name: string;
  inserted: number;
  updated: number;
  skipped: number;
}

export interface GrantAdminResult {
  tenantId: string;
  userId: string;
  username: string;
  stages: GrantAdminStage[];
  totalRows: number;
  durationMs: number;
  /** Present on re-runs that detected newly-added menus/permissions. */
  note?: string;
}

/* -------------------------------------------------------------------- */
/*  Factory                                                             */
/* -------------------------------------------------------------------- */

export function createMigrationService(client: AxiosInstance) {
  return {
    async run(input: MigrationRunInput): Promise<MigrationRunResult> {
      const r = await client.post<MigrationRunResult | { data: MigrationRunResult }>(
        '/admin/migration/run',
        normaliseMigrationRunInput(input),
      );
      return unwrap<MigrationRunResult>(r.data);
    },

    async seedMenus(input: SeedMenusInput): Promise<SeedMenusResult> {
      const r = await client.post<SeedMenusResult | { data: SeedMenusResult }>(
        '/admin/migration/seed-menus',
        input,
      );
      return unwrap<SeedMenusResult>(r.data);
    },

    async grantAdminEverything(input: GrantAdminInput): Promise<GrantAdminResult> {
      const r = await client.post<GrantAdminResult | { data: GrantAdminResult }>(
        '/admin/migration/grant-admin-everything',
        {
          ...input,
          include: { ...DEFAULT_INCLUDE, ...(input.include ?? {}) },
        },
      );
      return unwrap<GrantAdminResult>(r.data);
    },
  };
}

export type MigrationService = ReturnType<typeof createMigrationService>;

const DEFAULT_INCLUDE: Required<GrantAdminInclude> = {
  roles: true,
  permissions: true,
  menus: true,
  branches: true,
  warehouses: true,
};

/**
 * Fill in defaults the backend treats as required. Mirrors the
 * "Default `applicationName = 'KSS-BillBook'`" rule called out in §10.
 */
function normaliseMigrationRunInput(input: MigrationRunInput): MigrationRunInput {
  return {
    ...input,
    applicationName:
      !input.applicationName || input.applicationName === 'string'
        ? 'KSS-BillBook'
        : input.applicationName,
    integratedSecurity: input.integratedSecurity ?? false,
    trustServerCertificate: input.trustServerCertificate ?? true,
    encrypt: input.encrypt ?? true,
    minPoolSize: input.minPoolSize ?? 5,
    maxPoolSize: input.maxPoolSize ?? 50,
    connectTimeout: input.connectTimeout ?? 0,
  };
}

function unwrap<T>(d: T | { data: T }): T {
  if (d && typeof d === 'object' && 'data' in (d as Record<string, unknown>)) {
    const inner = (d as { data: T }).data;
    if (inner !== undefined) return inner;
  }
  return d as T;
}
